import { useState, useEffect, useRef } from 'react';
import { Loader2, AlertCircle, ImageIcon, Trash2, Upload, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { stateImageService, type StateImageRow } from '../../services/stateImageService';
import { STATE_CHART_FALLBACK } from '../../lib/stateMarketImage';

/**
 * The ASX market chart shown in section 2 ("the energy market") of the tender proposal, one
 * per state. Generation picks it from the states of the tender's own sites — nobody chooses
 * it per tender — so this screen is the only place it is set.
 *
 * The screen opens on the four states ASX publishes, plus anything already configured. That
 * is a starting point, not a limit: "Add state" takes any code, and the endpoints accept it,
 * so a state ASX adds later needs an upload rather than a release. A state with a chart gets
 * one; a state without shows none.
 */

/** Full names, purely cosmetic — an unlisted code still renders, just without its long name. */
const STATE_LABEL: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  ACT: 'Australian Capital Territory',
  TAS: 'Tasmania',
  WA: 'Western Australia',
  NT: 'Northern Territory',
};

/** Which states borrow this one's chart, phrased for the row that supplies it. */
function suppliesFor(state: string): string[] {
  return Object.entries(STATE_CHART_FALLBACK)
    .filter(([, target]) => target === state)
    .map(([source]) => source);
}

export const StateImagesTab = () => {
  const [images, setImages] = useState<StateImageRow[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyState, setBusyState] = useState<string | null>(null);
  // States the admin added by hand this session — they have no image yet, so the server
  // cannot know about them until one is uploaded.
  const [addedStates, setAddedStates] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [newState, setNewState] = useState('');
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const apply = (res: Awaited<ReturnType<typeof stateImageService.list>>) => {
    if (res.success && res.data) {
      setImages(res.data.images ?? []);
      setStates(res.data.states ?? []);
    } else {
      setError(res.error ?? 'Could not load state charts');
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    apply(await stateImageService.list());
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await stateImageService.list();
      if (!mounted) return;
      apply(res);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const imageFor = (state: string) => images.find(r => r.state_code === state) ?? null;

  const handleFile = async (state: string, file: File | undefined) => {
    if (!file) return;
    // Replacing is destructive — the old file is overwritten at the same storage path.
    if (imageFor(state) && !confirm(`Replace the ${state} market chart? The current one is overwritten.`)) return;

    setBusyState(state);
    setError(null);
    const res = await stateImageService.upload(state, file);
    if (res.success) await load();
    else setError(res.error ?? 'Upload failed');
    setBusyState(null);
  };

  const handleAddState = () => {
    const code = newState.trim().toUpperCase();
    if (!/^[A-Z]{2,3}$/.test(code)) {
      setError(`"${newState}" is not a state code. Enter 2-3 letters, e.g. NT.`);
      return;
    }
    if (states.includes(code) || addedStates.includes(code)) {
      setError(`${code} is already listed.`);
      return;
    }
    setAddedStates(prev => [...prev, code]);
    setNewState('');
    setAdding(false);
    setError(null);
  };

  const handleClear = async (state: string) => {
    if (!confirm(`Delete the ${state} market chart? Tenders in ${state} will show no chart.`)) return;
    setBusyState(state);
    setError(null);
    const res = await stateImageService.clear(state);
    if (res.success) await load();
    else setError(res.error ?? 'Could not delete the chart');
    setBusyState(null);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading state charts…
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ImageIcon className="h-4 w-4" />
          Market chart by state
        </CardTitle>
        <p className="text-sm text-gray-500">
          Shown in section 2 of the tender proposal, chosen from the states of the tender's own
          sites. A state with a chart gets one; a state without shows none. These are the four
          ASX Energy publishes — add any other state you need.
        </p>
      </CardHeader>

      <CardContent className="space-y-2">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {[...new Set([...states, ...addedStates])].sort().map(state => {
          const row = imageFor(state);
          const busy = busyState === state;
          const borrowsFrom = STATE_CHART_FALLBACK[state];
          // Shown faded: this state has no chart of its own but will use another's.
          const inherited = !row && borrowsFrom ? imageFor(borrowsFrom) : null;
          const supplies = suppliesFor(state);

          return (
            <div key={state} className="flex items-center gap-4 rounded-lg border border-gray-200 p-3">
              <div className="flex h-14 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50">
                {row || inherited
                  ? (
                    <img
                      src={(row ?? inherited)!.image_url}
                      alt={`${state} market chart`}
                      className={`h-full w-full object-contain${inherited ? ' opacity-50' : ''}`}
                    />
                  )
                  : <ImageIcon className="h-5 w-5 text-gray-300" />}
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {state}
                  {STATE_LABEL[state] && (
                    <span className="ml-2 font-normal text-gray-500">{STATE_LABEL[state]}</span>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  {row
                    ? `Set ${new Date(row.updated_at).toLocaleDateString('en-AU')}`
                    : inherited
                      ? `Using the ${borrowsFrom} chart — upload one to override`
                      : 'No chart — tenders in this state show none'}
                  {supplies.length > 0 && ` · also used for ${supplies.join(', ')}`}
                </p>
              </div>

              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                ref={el => { fileInputs.current[state] = el; }}
                onChange={e => {
                  handleFile(state, e.target.files?.[0]);
                  // Reset so re-picking the same file fires change again.
                  e.target.value = '';
                }}
              />

              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => fileInputs.current[state]?.click()}
              >
                {busy
                  ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                {row ? 'Replace' : 'Upload'}
              </Button>

              {row && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => handleClear(state)}
                  title={`Delete the ${state} chart`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-gray-400" />
                </Button>
              )}
            </div>
          );
        })}

        {/* Any state code is accepted — the four above are just where the screen starts. */}
        {adding ? (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 p-3">
            <input
              autoFocus
              value={newState}
              onChange={e => setNewState(e.target.value.toUpperCase().slice(0, 3))}
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddState();
                if (e.key === 'Escape') { setAdding(false); setNewState(''); }
              }}
              placeholder="NT"
              className="w-24 rounded-md border border-gray-200 px-2 py-1.5 text-sm uppercase"
            />
            <Button size="sm" onClick={handleAddState}>Add</Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setAdding(false); setNewState(''); setError(null); }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs text-gray-500">
              A state code as it appears on your sites — 2 or 3 letters.
            </span>
          </div>
        ) : (
          <Button variant="ghost" size="sm" className="text-gray-500" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add state
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
